import type { CSSProperties } from "react";

export type AnchoredPlacement = "above" | "below";
export type AnchoredAlign = "start" | "center" | "end";

const VIEWPORT_PADDING = 8;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

export function computeAnchoredPanelStyle({
  triggerRect,
  panelWidth,
  panelHeight,
  placement,
  align,
  gap,
}: {
  triggerRect: DOMRect;
  panelWidth: number;
  panelHeight: number;
  placement: AnchoredPlacement;
  align: AnchoredAlign;
  gap: number;
}): { style: CSSProperties; placement: AnchoredPlacement } {
  let resolvedPlacement = placement;

  const spaceBelow =
    window.innerHeight - triggerRect.bottom - gap - VIEWPORT_PADDING;
  const spaceAbove = triggerRect.top - gap - VIEWPORT_PADDING;

  if (
    panelHeight > 0 &&
    resolvedPlacement === "below" &&
    panelHeight > spaceBelow &&
    spaceAbove > spaceBelow
  ) {
    resolvedPlacement = "above";
  } else if (
    panelHeight > 0 &&
    resolvedPlacement === "above" &&
    panelHeight > spaceAbove &&
    spaceBelow > spaceAbove
  ) {
    resolvedPlacement = "below";
  }

  const style: CSSProperties = { position: "fixed" };

  if (align === "center") {
    const centerX = triggerRect.left + triggerRect.width / 2;
    const halfWidth = Math.max(panelWidth / 2, 0);
    style.left = clamp(
      centerX,
      VIEWPORT_PADDING + halfWidth,
      window.innerWidth - VIEWPORT_PADDING - halfWidth,
    );

    if (resolvedPlacement === "below") {
      const top = triggerRect.bottom + gap;
      style.top =
        panelHeight > 0
          ? clamp(
              top,
              VIEWPORT_PADDING,
              window.innerHeight - panelHeight - VIEWPORT_PADDING,
            )
          : top;
    } else {
      const anchorTop = triggerRect.top - gap;
      if (panelHeight > 0) {
        style.top = clamp(
          anchorTop - panelHeight,
          VIEWPORT_PADDING,
          window.innerHeight - panelHeight - VIEWPORT_PADDING,
        );
      } else {
        style.top = anchorTop;
        style.transform = "translate(-50%, -100%)";
        return { style, placement: resolvedPlacement };
      }
    }
    style.transform = "translate(-50%, 0)";
    return { style, placement: resolvedPlacement };
  }

  if (resolvedPlacement === "below") {
    const top = triggerRect.bottom + gap;
    style.top =
      panelHeight > 0
        ? clamp(
            top,
            VIEWPORT_PADDING,
            window.innerHeight - panelHeight - VIEWPORT_PADDING,
          )
        : top;
  } else {
    const top = triggerRect.top - gap - (panelHeight > 0 ? panelHeight : 0);
    style.top =
      panelHeight > 0
        ? clamp(
            top,
            VIEWPORT_PADDING,
            window.innerHeight - panelHeight - VIEWPORT_PADDING,
          )
        : triggerRect.top - gap;
  }

  if (align === "end") {
    let right = window.innerWidth - triggerRect.right;
    if (panelWidth > 0) {
      const leftEdge = window.innerWidth - right - panelWidth;
      if (leftEdge < VIEWPORT_PADDING) {
        right = window.innerWidth - panelWidth - VIEWPORT_PADDING;
      }
    }
    style.right = Math.max(VIEWPORT_PADDING, right);
  } else {
    let left = triggerRect.left;
    if (panelWidth > 0) {
      left = Math.min(
        left,
        window.innerWidth - panelWidth - VIEWPORT_PADDING,
      );
    }
    style.left = Math.max(VIEWPORT_PADDING, left);
  }

  return { style, placement: resolvedPlacement };
}
